import { computed, effectScope, inject, ref, watch } from "vue";
import { HttpStatus } from "#/shared/lib/http";
import { randomUUID } from "#/shared/utils/string";

const POLL_INTERVAL_MS = 4000;

export const STATUS = Object.freeze({
  IDLE: "idle",
  SUBMITTING: "submitting",
  PENDING: "pending",
  PROCESSING: "processing",
  COMPLETE: "complete",
  FAILED: "failed",
});

export const EMAIL_STATUS = Object.freeze({
  IDLE: "idle",
  SENDING: "sending",
  SENT: "sent",
});

export const DIRECTORY_PDF_EXPORT_KEY = Symbol("directory-pdf-export");

export function useDirectoryPdfExportStore() {
  const store = inject(DIRECTORY_PDF_EXPORT_KEY);
  if (!store) {
    throw new Error("Directory PDF export store must be provided by the app");
  }
  return store;
}

export function createDirectoryPdfExportStore({ storageKey = null } = {}) {
  const scope = effectScope();
  return scope.run(() => {
    let sessionStorageKey = storageKey;
    const status = ref(STATUS.IDLE);
    const downloadUrl = ref(null);
    const fieldErrors = ref({});
    const requestError = ref(null);
    const consent = ref(false);
    // True when a different search ran while this export was generating; the
    // ready screen then flags the PDF as from an earlier search.
    const searchChanged = ref(false);
    const submittedSearchSignature = ref(null);

    const idle = computed(() => status.value === STATUS.IDLE);
    const generating = computed(() => [STATUS.SUBMITTING, STATUS.PENDING, STATUS.PROCESSING].includes(status.value));
    const complete = computed(() => status.value === STATUS.COMPLETE);
    const failed = computed(() => status.value === STATUS.FAILED);

    // Email delivery is a separate lifecycle over the finished PDF: its state can
    // fail or reset without touching the completed export.
    const emailStatus = ref(EMAIL_STATUS.IDLE);
    const emailFieldErrors = ref({});
    const emailRequestError = ref(null);
    const preparerName = ref("");
    const emails = ref("");
    const emailConsent = ref(false);

    let api = null;
    let exportId = null;
    let emailToken = null;
    let emailIdempotencyKey = null;
    let submittedSearchParams = null;
    let pollTimer = null;
    // Invalidates in-flight polls after a reset, so a late response from an
    // abandoned export cannot overwrite the fresh state.
    let pollGeneration = 0;

    // Access to sessionStorage itself can throw in blocked/private contexts.
    function readSession() {
      try {
        return sessionStorageKey ? JSON.parse(window.sessionStorage.getItem(sessionStorageKey)) : null;
      } catch {
        return null;
      }
    }

    function persistSession() {
      if (!sessionStorageKey) {
        return;
      }
      try {
        if (!exportId) {
          window.sessionStorage.removeItem(sessionStorageKey);
          return;
        }
        window.sessionStorage.setItem(
          sessionStorageKey,
          JSON.stringify({
            version: 1,
            id: exportId,
            status: status.value,
            downloadUrl: downloadUrl.value,
            emailToken,
            searchParams: submittedSearchParams,
            searchSignature: submittedSearchSignature.value,
          }),
        );
      } catch {
        // Storage is optional; the in-memory export must still work.
      }
    }

    async function restore() {
      if (!idle.value) {
        return;
      }
      const saved = readSession();
      if (
        !saved ||
        saved.version !== 1 ||
        typeof saved.id !== "string" ||
        !saved.id ||
        typeof saved.searchParams !== "string" ||
        typeof saved.searchSignature !== "string"
      ) {
        persistSession();
        return;
      }
      const generation = pollGeneration;
      exportId = saved.id;
      emailToken = typeof saved.emailToken === "string" ? saved.emailToken : null;
      submittedSearchParams = saved.searchParams;
      submittedSearchSignature.value = saved.searchSignature;
      consent.value = true;
      // Never expose the cached download URL before the server verifies expiry.
      status.value = STATUS.SUBMITTING;
      try {
        const response = await api.status(exportId);
        if (generation === pollGeneration) {
          applyStatusResponse(response);
        }
      } catch (error) {
        if (generation !== pollGeneration) {
          return;
        }
        if ([404, 410].includes(error?.status)) {
          reset();
        } else {
          status.value = STATUS.FAILED;
          persistSession();
        }
      }
    }

    async function setStorageKey(storageKey) {
      if (sessionStorageKey || !storageKey) {
        return;
      }

      sessionStorageKey = storageKey;
      await restore();
    }

    // A field's server error is stale the moment the visitor edits that field, and
    // an edit after a send attempt clears its outcome so the Email PDF button is
    // re-armed for another send.
    watch(consent, () => clearFieldError(fieldErrors, "consent"));
    watch(preparerName, () => touchEmailField("preparer_name"));
    watch(emails, () => touchEmailField("emails"));
    watch(emailConsent, () => touchEmailField("consent"));

    function touchEmailField(key) {
      clearFieldError(emailFieldErrors, key);
      emailIdempotencyKey = null;
      emailRequestError.value = null;
      if (emailStatus.value === EMAIL_STATUS.SENT) {
        emailStatus.value = EMAIL_STATUS.IDLE;
      }
    }

    function clearFieldError(errorsRef, key) {
      delete errorsRef.value[key];
    }

    function parseEmails(value) {
      return value
        .split(",")
        .map((email) => email.trim())
        .filter(Boolean);
    }

    const canEmail = computed(() => emailConsent.value && parseEmails(emails.value).length > 0);

    function clearPollTimer() {
      clearTimeout(pollTimer);
      pollTimer = null;
    }

    function applyStatusResponse(response) {
      const nextStatus = response?.status;

      if (nextStatus === STATUS.COMPLETE) {
        status.value = STATUS.COMPLETE;
        downloadUrl.value = response.download_url ?? null;
        persistSession();
        return;
      }
      if (nextStatus === STATUS.FAILED) {
        status.value = STATUS.FAILED;
        persistSession();
        return;
      }

      status.value = nextStatus === STATUS.PROCESSING ? STATUS.PROCESSING : STATUS.PENDING;
      persistSession();
      schedulePoll();
    }

    function schedulePoll() {
      const generation = pollGeneration;

      clearPollTimer();
      pollTimer = setTimeout(async () => {
        let response;
        try {
          response = await api.status(exportId);
        } catch (error) {
          if (generation === pollGeneration) {
            if ([404, 410].includes(error?.status)) {
              reset();
            } else {
              status.value = STATUS.FAILED;
              persistSession();
            }
          }
          return;
        }

        if (generation === pollGeneration) {
          applyStatusResponse(response);
        }
      }, POLL_INTERVAL_MS);
    }

    async function submit(searchParams, searchSignature = submittedSearchSignature.value) {
      if (!consent.value || status.value === STATUS.SUBMITTING) {
        return;
      }

      const generation = pollGeneration;

      submittedSearchParams = searchParams;
      submittedSearchSignature.value = searchSignature;
      status.value = STATUS.SUBMITTING;
      fieldErrors.value = {};
      requestError.value = null;
      downloadUrl.value = null;

      let response;
      try {
        response = await api.create({ searchParams, consent: consent.value });
      } catch (error) {
        if (generation !== pollGeneration) {
          return;
        }

        status.value = STATUS.IDLE;
        if (error?.status === HttpStatus.UNPROCESSABLE_ENTITY) {
          const { consent: consentError, ...otherErrors } = error.data?.errors ?? {};
          fieldErrors.value = consentError ? { consent: consentError } : {};
          if (Object.keys(otherErrors).length > 0) {
            requestError.value = "generic";
          }
        } else if (error?.status === HttpStatus.TOO_MANY_REQUESTS) {
          requestError.value = "rate_limited";
        } else {
          requestError.value = "generic";
        }
        return;
      }

      if (generation !== pollGeneration) {
        return;
      }

      exportId = response?.id ?? null;
      emailToken = response?.email_token ?? null;
      applyStatusResponse(response);
    }

    async function emailPdf() {
      if (!canEmail.value || emailStatus.value !== EMAIL_STATUS.IDLE) {
        return;
      }

      const generation = pollGeneration;

      emailStatus.value = EMAIL_STATUS.SENDING;
      emailFieldErrors.value = {};
      emailRequestError.value = null;

      const submittedValues = emailFieldValues();
      emailIdempotencyKey ||= randomUUID();

      try {
        await api.email(exportId, {
          emails: parseEmails(emails.value),
          preparerName: preparerName.value.trim(),
          consent: emailConsent.value,
          emailToken,
          idempotencyKey: emailIdempotencyKey,
        });
      } catch (error) {
        if (generation !== pollGeneration) {
          return;
        }

        emailStatus.value = EMAIL_STATUS.IDLE;
        if (error?.status === HttpStatus.UNPROCESSABLE_ENTITY) {
          const errors = error.data?.errors ?? {};
          emailFieldErrors.value = unchangedFieldErrors(errors, submittedValues);
        } else if (error?.status === HttpStatus.TOO_MANY_REQUESTS) {
          emailRequestError.value = "rate_limited";
        } else {
          emailRequestError.value = "generic";
        }
        return;
      }

      if (generation === pollGeneration) {
        emailStatus.value = EMAIL_STATUS.SENT;
      }
    }

    function emailFieldValues() {
      return { preparer_name: preparerName.value, emails: emails.value, consent: emailConsent.value };
    }

    // The response describes the submitted payload; a field edited while the
    // request was in flight drops its error rather than wearing the old value's.
    function unchangedFieldErrors(errors, submittedValues) {
      const current = emailFieldValues();
      return Object.fromEntries(
        Object.entries(errors).filter(([key]) => key in submittedValues && current[key] === submittedValues[key]),
      );
    }

    // Regenerates the search the failed export was requested for, which may no
    // longer be the one on screen. A failed export has no poll in flight, so the
    // submit starts clean.
    function retry() {
      return submit(submittedSearchParams);
    }

    function reset() {
      pollGeneration += 1;
      clearPollTimer();
      exportId = null;
      emailToken = null;
      emailIdempotencyKey = null;
      submittedSearchParams = null;
      submittedSearchSignature.value = null;
      status.value = STATUS.IDLE;
      downloadUrl.value = null;
      fieldErrors.value = {};
      requestError.value = null;
      consent.value = false;
      searchChanged.value = false;
      emailStatus.value = EMAIL_STATUS.IDLE;
      emailFieldErrors.value = {};
      emailRequestError.value = null;
      preparerName.value = "";
      emails.value = "";
      emailConsent.value = false;
      persistSession();
    }

    return {
      status,
      downloadUrl,
      fieldErrors,
      requestError,
      consent,
      searchChanged,
      submittedSearchSignature,
      idle,
      generating,
      complete,
      failed,

      emailStatus,
      emailFieldErrors,
      emailRequestError,
      preparerName,
      emails,
      emailConsent,
      canEmail,

      configure: (exportApi) => (api = exportApi),
      submit,
      emailPdf,
      retry,
      reset,
      restore,
      setStorageKey,
      dispose() {
        pollGeneration += 1;
        clearPollTimer();
        scope.stop();
      },
    };
  });
}
